import {
  AppstoreOutlined,
  BellOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  MessageOutlined,
  PictureOutlined,
  SettingOutlined,
  UserOutlined
} from "@ant-design/icons"
import { Avatar, Badge, Drawer, Dropdown, Tooltip } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { NavLink, Outlet, useNavigate } from "react-router-dom"

import MessagingController from "../Messages/MessagingController.tsx"
import { BADGE_OVERFLOW, unreadTotalOf } from "../Messages/helpers/unread.ts"
import { fetchConversations } from "../Messages/redux/messaging.thunks.ts"
import Logo from "../../Images/logos/logo.svg?react"
import { RoutesPaths } from "../../constants/routes.ts"
import { cn } from "../../utils/cn.ts"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { logout as logoutThunk } from "../LoginPage/redux/auth.thunks.ts"
import { PortalThemeProvider } from "./components/PortalThemeProvider.tsx"
import {
  fetchAllProjectsDocuments,
  fetchAllProjectsInvoices,
  fetchAllProjectsPhotos,
  fetchPortalProjects
} from "./redux/portal.thunks.ts"

interface NavItem {
  to: string
  label: string
  icon: React.ReactNode
  /** Unread messages waiting behind this item, 0 when there is nothing. */
  badge?: number
}

/**
 * The red count beside a navigation label.
 *
 * `Badge` hides itself at zero, so no caller has to decide whether to render
 * one — and `overflowCount` keeps three digits from widening the row.
 */
function NavBadge({ count }: { count: number }) {
  return (
    <Badge
      count={count}
      overflowCount={BADGE_OVERFLOW}
      size={"small"}
      data-testid={"portal-unread-badge"}
    />
  )
}

function PortalLayout() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)

  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const projects = useAppSelector((state) => state.portal.projects)
  const projectsLoaded = useAppSelector((state) => state.portal.projectsLoaded)
  const conversations = useAppSelector((state) => state.messaging.conversations)
  const unreadMessages = unreadTotalOf(conversations)

  useEffect(() => {
    if (!projectsLoaded) void dispatch(fetchPortalProjects())

  }, [])

  // Fetched in the shell, not on the Messages page: the badge has to be right
  // on the first screen after a login, and a customer who never opens Messages
  // still needs to be told there is something there. The portal route returns
  // only the customer's own project conversations, so this cannot pull in an
  // internal room. The socket refreshes these counts on every reconnect.
  useEffect(() => {
    void dispatch(fetchConversations({ portal: true }))
  }, [dispatch])

  // Needs-your-attention counts on project cards, and the all-projects
  // Documents/Photos/Home surfaces, all read these — fetched once here
  // rather than by whichever page happens to mount first. A single-project
  // customer still needs this: Documents/Photos/Payments are the customer's
  // real entry points now (not a nested project tab), so even one project
  // must resolve there, not just "more than one".
  useEffect(() => {
    if (projectsLoaded && projects.length > 0) {
      const refs = projects.map((p) => ({ id: p.id, lead_name: p.lead_name }))
      void dispatch(fetchAllProjectsInvoices(refs))
      void dispatch(fetchAllProjectsDocuments(refs))
      void dispatch(fetchAllProjectsPhotos(refs))
    }
     
  }, [projectsLoaded, projects.length])

  const primaryNav: NavItem[] = [
    { to: RoutesPaths.portal.home, label: t("portal.nav.home"), icon: <HomeOutlined /> },
    {
      to: RoutesPaths.portal.projects,
      label: t("portal.nav.myProjects"),
      icon: <AppstoreOutlined />
    },
    {
      to: RoutesPaths.portal.calendar,
      label: t("portal.nav.calendar"),
      icon: <CalendarOutlined />
    },
    {
      to: RoutesPaths.portal.documents,
      label: t("portal.nav.documents"),
      icon: <FileTextOutlined />
    },
    {
      to: RoutesPaths.portal.photos,
      label: t("portal.nav.photos"),
      icon: <PictureOutlined />
    },
    {
      to: RoutesPaths.portal.messages,
      label: t("portal.nav.messages"),
      icon: <MessageOutlined />,
      badge: unreadMessages
    },
    {
      to: RoutesPaths.portal.payments,
      label: t("portal.nav.payments"),
      icon: <DollarOutlined />
    }
  ]

  // Home, My projects, Messages, Payments stay on the mobile tab bar;
  // Calendar, Documents, Photos and Settings move under More (NAV 01) — the
  // redesign brief explicitly keeps Calendar out of the mobile tab bar too.
  const mobilePrimary = [primaryNav[0], primaryNav[1], primaryNav[5], primaryNav[6]]
  const moreItems: NavItem[] = [
    primaryNav[2],
    primaryNav[3],
    primaryNav[4],
    {
      to: RoutesPaths.portal.settings,
      label: t("portal.nav.settings"),
      icon: <SettingOutlined />
    }
  ]

  const handleLogout = () => {
    void dispatch(logoutThunk({ navigate }))
  }

  const sidebarNavItemClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex min-h-11 items-center gap-2.5 rounded-lg px-3 text-[15px] font-medium text-gray-600 hover:bg-gray-50 [&_.anticon]:text-[20px]",
      isActive && "bg-primary/10 font-semibold text-primary"
    )

  return (
    <PortalThemeProvider>
      {/* `100dvh` rather than `min-h-screen`'s `100vh` — `vh` is pinned to the
       * largest possible viewport and ignores a browser chrome that shows/
       * hides on scroll, which can leave the shell short of the real,
       * currently-visible viewport. Explicit `w-full` at each level so no
       * ancestor's implicit width can ever leave a gap next to the sidebar. */}
      {/* The portal's own messaging socket, in portal mode — so the customer's
          unread count stays live on every portal page, not just Messages. */}
      <MessagingController portal />
      <div className="flex min-h-[100dvh] w-full bg-background">
        {/* Desktop sidebar */}
        <aside className="hidden w-56 shrink-0 flex-col border-r border-gray-200 bg-white lg:flex">
          {/* `w-auto` on this SVG resolves via flexbox cross-axis stretch
           * (this column's default `align-items: stretch`) rather than the
           * logo's own intrinsic ratio, so it rendered at the container's
           * full width with the artwork letterboxed tiny inside it. An
           * explicit width with `h-auto` sizes from the SVG's real 81:34
           * ratio instead — verified against the asset's own viewBox. */}
          <div className="flex flex-col gap-1.5 px-6 pb-4 pt-6">
            <Logo className="h-auto w-[132px]" aria-label="EZBuild" />
            <span className="text-xs font-medium text-gray-400">
              {t("portal.nav.customerPortal")}
            </span>
          </div>

          <nav className="flex flex-1 flex-col gap-0.5 px-3 py-2">
            {primaryNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={sidebarNavItemClass}>
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {item.badge ? <NavBadge count={item.badge} /> : null}
              </NavLink>
            ))}
          </nav>

          <div className="flex flex-col gap-0.5 border-t border-gray-100 px-3 py-3">
            <NavLink to={RoutesPaths.portal.settings} className={sidebarNavItemClass}>
              <>
                <SettingOutlined />
                {t("portal.nav.settings")}
              </>
            </NavLink>
            <button
              type="button"
              onClick={handleLogout}
              className="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-left text-[15px] font-medium text-gray-600 hover:bg-gray-50 [&_.anticon]:text-[20px]"
            >
              <LogoutOutlined />
              {t("portal.nav.signOut")}
            </button>
          </div>
        </aside>

        <div className="flex min-h-[100dvh] w-full min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 w-full shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 lg:hidden">
              <Logo className="h-7 w-auto" aria-label="EZBuild" />
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-4">
              <Tooltip title={t("portal.notifications.comingSoon")}>
                <BellOutlined aria-hidden className="text-lg text-gray-400" role="img" />
              </Tooltip>

              <Dropdown
                menu={{
                  items: [
                    {
                      key: "settings",
                      label: t("portal.nav.settings"),
                      icon: <SettingOutlined />,
                      onClick: () => navigate(RoutesPaths.portal.settings)
                    },
                    {
                      key: "logout",
                      label: t("portal.nav.signOut"),
                      icon: <LogoutOutlined />,
                      onClick: handleLogout
                    }
                  ]
                }}
                placement="bottomRight"
              >
                <button
                  type="button"
                  className="flex items-center gap-2"
                  aria-label={t("portal.nav.account")}
                >
                  <Avatar src={userInfo?.avatar || undefined} icon={<UserOutlined />} />
                  <span className="hidden text-sm font-medium text-gray-700 lg:inline">
                    {userInfo?.first_name}
                  </span>
                </button>
              </Dropdown>
            </div>
          </header>

          <main className="w-full min-w-0 flex-1 bg-background pb-20 lg:pb-0">
            <Outlet />
          </main>
        </div>

        <nav
          className="fixed inset-x-0 bottom-0 z-30 flex h-16 items-stretch border-t border-gray-200 bg-white lg:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {mobilePrimary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex min-w-11 flex-1 flex-col items-center justify-center gap-0.5 text-xs text-gray-500",
                  isActive && "text-primary"
                )
              }
            >
              {/* The badge rides the icon rather than the label, so a count
                  cannot push the tab's text out of its column. */}
              <span className="relative text-lg">
                {item.icon}
                {item.badge ? (
                  <span className="absolute -right-2.5 -top-1">
                    <NavBadge count={item.badge} />
                  </span>
                ) : null}
              </span>
              {item.label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex min-w-11 flex-1 flex-col items-center justify-center gap-0.5 text-xs text-gray-500"
          >
            <MenuOutlined className="text-lg" />
            {t("portal.nav.more")}
          </button>
        </nav>

        <Drawer
          title={t("portal.nav.more")}
          placement="bottom"
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          height="auto"
        >
          <div className="flex flex-col gap-1">
            {moreItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700",
                    isActive && "bg-primary/10 text-primary"
                  )
                }
              >
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {item.badge ? <NavBadge count={item.badge} /> : null}
              </NavLink>
            ))}
          </div>
        </Drawer>
      </div>
    </PortalThemeProvider>
  )
}

export default PortalLayout
